import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowRight, MessageCircle, Check } from "lucide-react";
import SEO from "../components/SEO";
import PricingProof from "../components/PricingProof";
import { serviceSchema, faqSchema } from "../data/schema";
import { Reveal, Stagger, Item, Chapter, MaskedLine } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { INDUSTRIES } from "../data/industries";
import { waLink, CONTEXT_MESSAGES } from "../lib/whatsapp";
import { track } from "../lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function IndustryPage() {
  const { pathname } = useLocation();
  const clean = pathname.replace(/\/+$/, "");
  const industry = INDUSTRIES.find((i) => `/website-development-for-${i.slug}` === clean);
  if (!industry) return <Navigate to="/" replace />;
  const slug = industry.slug;

  const path = `/website-development-for-${industry.slug}`;

  return (
    <main className="pt-36 lg:pt-44">
      <SEO
        title={industry.metaTitle}
        description={industry.metaDesc}
        path={path}
        schema={[
          serviceSchema({ name: industry.heroTitle, description: industry.metaDesc, path, serviceType: "Website development" }),
          faqSchema(industry.faqs),
        ]}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: `For ${industry.name}`, path }]}
      />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="industry-hero">
        <nav className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <span className="mx-2 text-ember">/</span>
          <span className="text-white/70">For {industry.name}</span>
        </nav>
        <h1 className="mt-8 max-w-4xl font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLine>{industry.heroTitle}</MaskedLine>
        </h1>
        <Reveal hero delay={0.25}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
            {industry.intro}
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <button
              data-testid="industry-primary-cta"
              onClick={() => { track("cta_click", { location: "industry", industry: slug }); openLeadPopup(); }}
              className="group flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
            >
              Get a Free Website Plan
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={waLink(CONTEXT_MESSAGES[industry.waKey] || CONTEXT_MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="industry-whatsapp-cta"
              onClick={() => track("whatsapp_click", { location: "industry_page", industry: slug })}
              className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <Chapter index="01" label={`What We Build for ${industry.name}`} />
        <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {industry.benefits.map((b) => (
            <Item key={b.h} className="rounded-2xl border border-white/10 bg-ink p-7">
              <Check className="h-5 w-5 text-ember" />
              <h2 className="mt-4 font-display text-lg font-bold tracking-tight">{b.h}</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/50">{b.p}</p>
            </Item>
          ))}
          <Item className="flex flex-col justify-between rounded-2xl bg-ember p-7">
            <p className="font-display text-xl font-bold leading-snug tracking-tight">
              We don't just build websites. We build websites designed to bring you business.
            </p>
            <button
              data-testid="industry-mid-cta"
              onClick={openLeadPopup}
              className="mt-6 flex w-fit items-center gap-2 rounded-full bg-white px-5 py-3 font-display text-xs font-bold uppercase tracking-wider text-obsidian"
            >
              Discuss My Business <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </Item>
        </Stagger>
      </section>

      <section className="border-t border-line bg-ink py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <Chapter index="02" label="Questions" />
          <div className="mt-8">
            <Accordion type="single" collapsible data-testid="industry-faq">
              {industry.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-white hover:text-ember hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-white/55">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <Reveal>
            <div className="mt-12 rounded-3xl bg-bone p-8 text-obsidian sm:p-10">
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                Ready for a website that brings your {industry.name.toLowerCase().replace(/s$/, "")} business more enquiries?
              </h2>
              <p className="mt-3 max-w-md text-sm text-obsidian/60">
                Tell us about your business — you'll get a website plan designed around generating
                enquiries, not just looking good.
              </p>
              <button
                data-testid="industry-bottom-cta"
                onClick={openLeadPopup}
                className="mt-7 rounded-full bg-ember px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover"
              >
                Get My Website Plan
              </button>
            </div>
          </Reveal>
        </div>
      </section>
      <PricingProof heading={`Pricing & recent work for ${industry.name.toLowerCase()}`} />
    </main>
  );
}
